<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import redeMarketingApi from '@/services/api/redeMarketingRepository'
import TreeVisual from './TreeVisual.vue'
import MembroDetalhesModal from './MembroDetalhesModal.vue'

export default {
  components: { TreeVisual, MembroDetalhesModal },
  setup() {
    const route = useRoute()
    const membroIdInput = ref(null)
    const no = ref(null)
    const upline = ref([])
    const downline = ref([])
    const totalDiretos = ref(0)
    const loading = ref(false)
    const mensagem = ref('')
    const erro = ref(false)
    const modalAberto = ref(false)
    const membroSelecionadoId = ref(null)

    function abrirDetalhes(id) {
      membroSelecionadoId.value = id
      modalAberto.value = true
    }

    const downlinePorNivel = computed(() => {
      const mapa = new Map()
      downline.value.forEach((d) => {
        if (!mapa.has(d.nivel)) mapa.set(d.nivel, [])
        mapa.get(d.nivel).push(d)
      })
      return Array.from(mapa.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([nivel, itens]) => ({ nivel, itens }))
    })

    async function carregar() {
      if (!membroIdInput.value) return
      loading.value = true
      mensagem.value = ''
      erro.value = false
      try {
        const id = membroIdInput.value
        const [noResp, upResp, downResp, diretosResp] = await Promise.all([
          redeMarketingApi.BuscarNo(id),
          redeMarketingApi.Upline(id),
          redeMarketingApi.Downline(id),
          redeMarketingApi.ContarDiretos(id),
        ])
        no.value = noResp.data
        upline.value = upResp.data
        downline.value = downResp.data
        totalDiretos.value = diretosResp.data
      } catch (e) {
        erro.value = true
        mensagem.value = 'Erro ao carregar: ' + (e.response?.data?.message || e.message)
        no.value = null
      } finally {
        loading.value = false
      }
    }

    watch(
      () => route.params.membroId,
      (v) => {
        if (v) {
          membroIdInput.value = Number(v)
          carregar()
        }
      },
      { immediate: false },
    )

    onMounted(() => {
      if (route.params.membroId) {
        membroIdInput.value = Number(route.params.membroId)
        carregar()
      }
    })

    return {
      membroIdInput,
      no,
      upline,
      downline,
      totalDiretos,
      loading,
      mensagem,
      erro,
      modalAberto,
      membroSelecionadoId,
      abrirDetalhes,
      downlinePorNivel,
      carregar,
    }
  },
}
</script>

<template>
  <div>
    <div class="d-flex align-items-center gap-2 mb-3">
      <router-link to="/rede-marketing" class="btn btn-sm btn-light">
        <i class="material-icons align-middle">arrow_back</i>
      </router-link>
      <h3 class="mb-0">Genealogia</h3>
    </div>

    <div class="d-flex gap-2 mb-3">
      <input v-model.number="membroIdInput" type="number"
             placeholder="ID do membro" class="form-control form-control-sm" style="max-width:200px;"
             @keyup.enter="carregar" />
      <button class="btn btn-sm btn-primary d-flex align-items-center" @click="carregar" :disabled="loading">
        <i class="material-icons me-1" style="font-size: 18px;">search</i>
        Visualizar
      </button>
    </div>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary"></div>
    </div>

    <template v-else-if="no">
      <!-- Árvore visual -->
      <div class="card mb-3">
        <div class="card-header d-flex justify-content-between align-items-center pb-2">
          <h5 class="mb-0"><i class="material-icons align-middle me-1">account_tree</i> Visualização em árvore</h5>
          <small class="text-muted">clique numa bolinha para ver detalhes</small>
        </div>
        <div class="card-body">
          <TreeVisual :no-central="no" :downline="downline" @no-clicado="abrirDetalhes" />
        </div>
      </div>

      <!-- Card membro central -->
      <div class="card mb-3 card-central text-white card-clicavel"
           @click="abrirDetalhes(no.membroId)"
           title="Clique para ver detalhes">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h5 class="mb-1 text-white">
                <i class="material-icons align-middle">person</i> {{ no.nomeMembro }}
                <span class="badge-id ms-2">ID #{{ no.membroId }}</span>
              </h5>
              <small>Código: <code class="bg-white bg-opacity-25 text-white px-2 rounded">{{ no.codigoIndicacao }}</code></small>
            </div>
            <div class="d-flex gap-4">
              <div class="text-center">
                <div class="h4 mb-0">{{ no.nivel }}</div>
                <small>Nível</small>
              </div>
              <div class="text-center">
                <div class="h4 mb-0">{{ totalDiretos }}</div>
                <small>Diretos</small>
              </div>
              <div class="text-center">
                <div class="h4 mb-0">{{ downline.length }}</div>
                <small>Downline total</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Upline -->
      <div class="card mb-3">
        <div class="card-header pb-2">
          <h5 class="mb-0"><i class="material-icons align-middle me-1">arrow_upward</i> Upline ({{ upline.length }})</h5>
        </div>
        <div class="card-body">
          <p v-if="upline.length === 0" class="small text-muted mb-0">Membro é raiz da árvore.</p>
          <table v-else class="table table-sm mb-0">
            <thead><tr class="small text-uppercase text-muted"><th>Nível</th><th>ID</th><th>Nome</th><th>Código</th></tr></thead>
            <tbody>
              <tr v-for="u in upline" :key="u.membroId">
                <td><span class="badge bg-primary">Nível {{ u.nivel }}</span></td>
                <td class="text-muted fw-bold">#{{ u.membroId }}</td>
                <td>{{ u.nomeMembro }}</td>
                <td><code class="text-dark">{{ u.codigoIndicacao }}</code></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Downline agrupada -->
      <div class="card">
        <div class="card-header pb-2">
          <h5 class="mb-0"><i class="material-icons align-middle me-1">arrow_downward</i> Downline ({{ downline.length }})</h5>
        </div>
        <div class="card-body">
          <p v-if="downline.length === 0" class="small text-muted mb-0">Ainda sem indicados.</p>
          <div v-else>
            <div v-for="g in downlinePorNivel" :key="g.nivel" class="mb-3">
              <div class="small text-uppercase text-muted mb-2">
                Nível {{ g.nivel }} — {{ g.itens.length }} {{ g.itens.length === 1 ? 'membro' : 'membros' }}
              </div>
              <table class="table table-sm table-hover mb-0">
                <tbody>
                  <tr v-for="d in g.itens" :key="d.membroId">
                    <td class="text-muted fw-bold" style="width: 60px;">#{{ d.membroId }}</td>
                    <td>{{ d.nomeMembro }}</td>
                    <td><code class="text-dark">{{ d.codigoIndicacao }}</code></td>
                    <td class="text-end">
                      <button class="btn btn-sm btn-outline-primary" @click="abrirDetalhes(d.membroId)">
                        <i class="material-icons" style="font-size: 16px;">visibility</i>
                      </button>
                      <router-link :to="`/rede-marketing/genealogia/${d.membroId}`"
                                   class="btn btn-sm btn-outline-info ms-1">
                        <i class="material-icons" style="font-size: 16px;">account_tree</i>
                      </router-link>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <MembroDetalhesModal :visible="modalAberto" :membro-id="membroSelecionadoId" @fechar="modalAberto = false" />
    </template>

    <div v-if="mensagem" class="alert mt-3" :class="erro ? 'alert-danger' : 'alert-success'">
      {{ mensagem }}
    </div>
  </div>
</template>

<style scoped>
.card-central {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border: none;
  box-shadow: 0 4px 12px rgba(29, 78, 216, 0.3);
}
.card-clicavel {
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
}
.card-clicavel:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(29, 78, 216, 0.4);
}
.badge-id {
  background: rgba(255, 255, 255, 0.25);
  color: #fff;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 0.72rem;
  font-weight: 600;
  font-family: monospace;
  vertical-align: middle;
}
</style>
